/**
 * Sync-engine provider — web wiring for `@shopista/shared/sync`.
 *
 * T12.0.17 Phase 1 lands the engine in `apps/web`. The factory wraps
 * `createSyncEngine` with:
 *   - Web `IStorage` (Dexie via `createDexieStorage` — re-used from the
 *     storage adapter built in T6).
 *   - Fetch-backed `SyncApi` calling `/api/v2/sync` and
 *     `/api/v2/sync/full-resync` with the Bearer token from `WebTokenStorage`.
 *   - `onHouseholdEvent` that invalidates the TanStack Query
 *     `households.selectedStores` cache key so any mounted reader (Stores
 *     card, Settings page) refetches the post-LWW blob.
 *   - `isOnline` from `navigator.onLine`.
 *   - `now` from `Date.now`.
 *   - `logger` routed to `console.debug` so production builds stay quiet.
 *
 * Lifecycle helper `attachEngineLifecycle` wires:
 *   - A foreground periodic `engine.pull()` every 30 s (default). Routine
 *     interval polls are silent — they do NOT emit `pulling`, so the
 *     `SyncStatusIndicator` (D5) stays hidden during steady-state foreground.
 *   - `document.visibilitychange` — pauses the interval while hidden, fires
 *     an immediate pull on tab-return (loud, emits `pulling`) and restarts
 *     the interval (D3).
 *   - An external `pullNow(reason)` helper used by the caller for initial
 *     mount seed (`'mount'`) and the SSE subscriber's tab-return path
 *     (`'tab-return'`); both are loud.
 *   - Returns a disposer the caller invokes on logout / unmount; the
 *     disposer clears the interval, removes the listener, and clears any
 *     in-flight throttle state.
 *
 * Device-id persistence lives here (not in tokenStorage) — the engine needs
 * a stable, per-browser `deviceId` so the server's "don't echo a device's
 * own pull events" filter works. The id is generated once and stored in
 * `localStorage` under `shopista-device-id`. Clearing storage (cache wipe,
 * private window) cuts a new id; that's correct behaviour — the device is
 * effectively new.
 */

import type { QueryClient } from '@tanstack/react-query';
import { keys } from '@shopista/shared/api/hooks';
import { HttpClient, type TokenProvider } from '@shopista/shared/api';
import {
  createSyncEngine,
  type FullResyncPayload,
  type SyncApi,
  type SyncEngine,
  type SyncIdentity,
  type SyncRequestV2,
  type SyncResponseV2,
} from '@shopista/shared/sync';
import type { IStorage } from '@shopista/storage/interface';
import { createDexieStorage } from '@shopista/storage/web';
import { makeWebRefreshLock } from './webRefreshLock';
import { toFetchAdapter } from './fetchAdapter';
import { randomUuid } from './randomUuid';

// ---------------------------------------------------------------------------
// Device identity
// ---------------------------------------------------------------------------

/** localStorage key holding the persistent per-browser device id. */
export const DEVICE_ID_KEY = 'shopista-device-id';

/**
 * Read the stored device id, minting a fresh one on first call. The id is
 * a UUIDv4-shaped random string — uniqueness across devices is what matters,
 * not cryptographic strength.
 */
export function getOrCreateDeviceId(
  storage: Pick<Storage, 'getItem' | 'setItem'> = window.localStorage
): string {
  try {
    const existing = storage.getItem(DEVICE_ID_KEY);
    if (existing && existing.length > 0) return existing;
  } catch {
    // Quota / disabled — fall through and mint anyway. The id won't survive
    // a reload but the in-memory engine still works for the session.
  }
  // Single id helper (BUG35): feature-detects crypto.randomUUID, else builds a
  // v4 UUID from getRandomValues, else a Math.random fallback. One fallback, not
  // two — and the fallback is now UUID-shaped instead of the old `dev-…` form.
  const minted = randomUuid();
  try {
    storage.setItem(DEVICE_ID_KEY, minted);
  } catch {
    // ignore
  }
  return minted;
}

// ---------------------------------------------------------------------------
// SyncApi over fetch
// ---------------------------------------------------------------------------

export interface WebSyncApiOptions {
  /** Backend base URL (no trailing slash). Same value the HTTP client uses. */
  apiBaseUrl: string;
  /** Token provider — Bearer token read on every request. */
  tokens: TokenProvider;
  /** `fetch` impl, injected for tests. Defaults to `window.fetch`. */
  fetch?: typeof globalThis.fetch;
}

/**
 * Build a `SyncApi` that posts to `/api/v2/sync` and GETs
 * `/api/v2/sync/full-resync`. Both endpoints already exist; this is the
 * thin client side of the wire.
 *
 * T12.0.17 Round 5 — these requests run through the shared `HttpClient`
 * (the same one the REST orchestrators use) instead of a raw `fetch`. The
 * raw fetch path had no 401-refresh-retry, so a soft-expired access token
 * caused the engine's periodic pull to throw 401 every 30 s while every
 * other endpoint kept working (HttpClient transparently refreshed the
 * token + retried). Routing through HttpClient gives the engine:
 *   - Authorization header + `credentials: 'include'` (refresh cookie).
 *   - 401 → POST /api/auth/refresh → retry with new access token.
 *   - Shared `refreshPromise` so concurrent 401s collapse to one refresh.
 *   - `shopista:session-cleared` dispatch on refresh failure — the app
 *     redirects to `/login` instead of looping 401s in the console.
 * Response bodies are still typed via the engine's `SyncResponseV2` /
 * `FullResyncPayload` casts; HttpClient's schema slot accepts the body
 * un-validated (`z.any()`), preserving the previous wire contract.
 */
export interface WebSyncApiBundle {
  api: SyncApi;
  /**
   * Public refresh handle off the underlying `HttpClient` — shares the
   * deduped `refreshPromise`. The SSE subscriber awaits it before every
   * (re)connect so a soft-expired access token is refreshed through the same
   * path the REST pipeline uses (T12.0.23 D3).
   */
  refresh: () => Promise<string | null>;
}

export function createWebSyncApi(opts: WebSyncApiOptions): WebSyncApiBundle {
  const fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const http = new HttpClient({
    baseUrl: opts.apiBaseUrl,
    fetch: toFetchAdapter(fetchImpl),
    tokens: opts.tokens,
    withCredentials: true,
    // BUG15: same shared Web Locks refresh mutex as the app adapter's client
    // so the engine's 401-refresh + the SSE reconnect refresh never race the
    // app client (or another tab) into a rotation-losing double refresh.
    lock: makeWebRefreshLock(),
  });
  const api: SyncApi = {
    async sync(request: SyncRequestV2): Promise<SyncResponseV2> {
      const body = await http.postRaw('/api/v2/sync', request);
      return body as SyncResponseV2;
    },
    async fullResync(): Promise<FullResyncPayload> {
      const body = await http.getRaw('/api/v2/sync/full-resync');
      return body as FullResyncPayload;
    },
  };
  return { api, refresh: () => http.refresh() };
}

// ---------------------------------------------------------------------------
// Engine factory
// ---------------------------------------------------------------------------

export interface CreateWebSyncEngineOptions {
  /** Backend base URL. */
  apiBaseUrl: string;
  /** TokenProvider — used by the API adapter. */
  tokens: TokenProvider;
  /** TanStack QueryClient — invalidated on household-event arrival. */
  queryClient: QueryClient;
  /**
   * Optional storage override (defaults to Dexie). Tests pass a memory
   * impl; production passes nothing.
   */
  storage?: IStorage;
  /** Optional fetch override for tests. */
  fetch?: typeof globalThis.fetch;
}

export interface WebSyncEngineBundle {
  engine: SyncEngine;
  storage: IStorage;
  /**
   * Shared refresh handle off the underlying `HttpClient` (T12.0.23 D3). The
   * SSE subscriber awaits it before every (re)connect; it shares the deduped
   * `refreshPromise` with the REST 401-refresh path so a soft-expired access
   * token is refreshed through a single `/api/auth/refresh` call.
   */
  refresh: () => Promise<string | null>;
}

/**
 * Construct a `SyncEngine` wired for the web app. Returns the engine, the
 * underlying storage handle so the lifecycle helper can dispose both, and the
 * shared `refresh` handle for the SSE subscriber (T12.0.23 D3).
 */
export function createWebSyncEngine(
  opts: CreateWebSyncEngineOptions
): WebSyncEngineBundle {
  const storage = opts.storage ?? createDexieStorage();
  const { api, refresh } = createWebSyncApi({
    apiBaseUrl: opts.apiBaseUrl,
    tokens: opts.tokens,
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
  });

  const engine = createSyncEngine({
    storage,
    api,
    now: () => Date.now(),
    isOnline: () =>
      typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
        ? navigator.onLine
        : true,
    logger: (msg, meta) => {
      if (meta !== undefined) {
        // eslint-disable-next-line no-console
        console.debug('[sync]', msg, meta);
      } else {
        // eslint-disable-next-line no-console
        console.debug('[sync]', msg);
      }
    },
    onHouseholdEvent: (event) => {
      // T12.0.17 Phase 1 — `households.store_preferences` mirror lives in
      // TanStack Query (`useSelectedStores`, Stores card, Settings page).
      // Invalidating the key triggers a refetch on every mounted consumer
      // so the next render sees the post-LWW blob. The household-event
      // payload carries the same blob, but invalidation keeps the path
      // simple: the cache always reflects the server's view, never a
      // partial derivation done on the client.
      if (event.actionType === 'household_store_preferences_changed') {
        void opts.queryClient.invalidateQueries({
          queryKey: keys.households.selectedStores,
        });
      }
    },
    onShoppingListItemEvent: () => {
      // T12.0.17 fix #3 — the engine writes to Dexie, but the UI reads
      // through TanStack Query (`useActiveShoppingList`, `useShoppingLists`).
      // Without invalidation the cache holds stale data until the next
      // mutation, focus refetch, or 30 s pull lands. Invalidate both
      // shopping-list keys the orchestrator invalidates after a local
      // write — same key shape, same UI consumers, parity with the
      // optimistic-write path.
      void opts.queryClient.invalidateQueries({
        queryKey: keys.ShoppingLists.all,
      });
      void opts.queryClient.invalidateQueries({
        queryKey: keys.ShoppingLists.active,
      });
    },
    onHouseholdProductEvent: () => {
      // T12.0.17 fix #3 — same shape as items: the engine wrote to Dexie,
      // but `useHouseholdProducts` / `useHouseholdProductsSearch` /
      // `useHouseholdProduct` read through TanStack. Invalidating the
      // family root (`HouseholdProducts.all`) cascades to every key under
      // it including `one(productId)` and `search(query)`, matching the
      // post-mutation invalidation in `useHouseholdProducts.ts`.
      void opts.queryClient.invalidateQueries({
        queryKey: keys.HouseholdProducts.all,
      });
    },
  });

  return { engine, storage, refresh };
}

// ---------------------------------------------------------------------------
// Lifecycle — periodic pull + visibility handling
// ---------------------------------------------------------------------------

export interface AttachEngineLifecycleOptions {
  /** Engine to run pulls against. */
  engine: SyncEngine;
  /** Identity resolver — re-read on every pull (token / userId may change). */
  identity: () => SyncIdentity | null;
  /**
   * Foreground polling interval in ms. Default 30_000 per D3.
   */
  pollIntervalMs?: number;
  /**
   * Visibility readout. Tests inject a fake; production uses
   * `() => document.visibilityState === 'visible'`.
   */
  isVisible?: () => boolean;
  /**
   * `addEventListener` / `removeEventListener` target. Defaults to `document`
   * for the visibilitychange event.
   */
  visibilityTarget?: {
    addEventListener: (type: string, listener: () => void) => void;
    removeEventListener: (type: string, listener: () => void) => void;
  };
  /**
   * Optional hook fired whenever a pull is about to start / finishes /
   * errors. The sync-status indicator subscribes to this.
   */
  onPullStateChange?: (state: PullLifecycleState) => void;
  /** Optional logger; defaults to a no-op. */
  logger?: (msg: string, meta?: unknown) => void;
}

export type PullLifecycleState = 'idle' | 'pulling' | 'error';

/**
 * Why a pull fired. Drives whether the lifecycle emits `pulling` so the
 * `SyncStatusIndicator` (D5) lights:
 *   - `'mount'`        — initial seed on engine attach; user is staring at a
 *                        fresh page, so the badge surfaces "catching up".
 *   - `'tab-return'`   — user re-foregrounded; same reasoning as `mount`.
 *   - `'routine'`      — periodic 30 s interval poll during steady-state
 *                        foreground; AC says the indicator stays hidden.
 */
export type PullReason = 'mount' | 'tab-return' | 'routine';

export interface EngineLifecycleHandle {
  /**
   * Trigger an immediate pull. The optional `reason` controls whether the
   * indicator lights — defaults to `'tab-return'` so external callers
   * (initial seed, user-facing re-sync) light the indicator without
   * needing to pass an explicit reason.
   */
  pullNow: (reason?: PullReason) => Promise<void>;
  /** Tear down: clears interval, removes visibilitychange listener. */
  dispose: () => void;
}

const DEFAULT_POLL_INTERVAL_MS = 30_000;

/**
 * Wire periodic + visibility-aware pulls to an engine instance. The returned
 * handle's `dispose()` removes every side-effect — call it on logout / engine
 * teardown.
 */
export function attachEngineLifecycle(
  opts: AttachEngineLifecycleOptions
): EngineLifecycleHandle {
  const intervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const log = opts.logger ?? (() => {});
  const isVisible =
    opts.isVisible ??
    (() =>
      typeof document !== 'undefined'
        ? document.visibilityState === 'visible'
        : true);
  const visibilityTarget =
    opts.visibilityTarget ??
    (typeof document !== 'undefined'
      ? document
      : {
          addEventListener: () => {},
          removeEventListener: () => {},
        });

  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight = false;
  let disposed = false;

  const emit = (state: PullLifecycleState): void => {
    if (disposed) return; // no state callback after dispose()
    opts.onPullStateChange?.(state);
  };

  const doPull = async (reason: PullReason): Promise<void> => {
    if (disposed) return;
    if (inFlight) return;
    const identity = opts.identity();
    if (!identity) return;
    inFlight = true;
    // D5: routine 30 s interval polls stay silent so the indicator only
    // lights for user-visible catch-up (initial mount + tab-return). The
    // error path stays loud regardless — a failed routine poll still tells
    // the user something is wrong.
    const loud = reason !== 'routine';
    if (loud) emit('pulling');
    try {
      await opts.engine.pull(identity);
      if (loud) emit('idle');
    } catch (err) {
      log('lifecycle: pull error', { error: describe(err) });
      emit('error');
    } finally {
      inFlight = false;
    }
  };

  const startTimer = (): void => {
    if (timer !== null) return;
    timer = setInterval(() => {
      // Re-check visibility per tick so a tab backgrounded after the timer
      // started skips its turns instead of spamming pulls.
      if (!isVisible()) return;
      void doPull('routine');
    }, intervalMs);
  };

  const stopTimer = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const onVisibilityChange = (): void => {
    if (isVisible()) {
      // Tab-return per D3: fire an immediate pull (loud — indicator lights
      // until the catch-up lands), then restart the foreground polling
      // interval.
      void doPull('tab-return');
      startTimer();
    } else {
      stopTimer();
    }
  };

  visibilityTarget.addEventListener('visibilitychange', onVisibilityChange);

  // Initial state: if the tab is visible at attach-time, start the timer.
  // We do NOT fire a pull here — the caller decides when to seed the cursor.
  if (isVisible()) {
    startTimer();
  }

  return {
    async pullNow(reason: PullReason = 'tab-return'): Promise<void> {
      await doPull(reason);
    },
    dispose(): void {
      disposed = true;
      stopTimer();
      visibilityTarget.removeEventListener(
        'visibilitychange',
        onVisibilityChange
      );
    },
  };
}

function describe(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}
